import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import SiteFooter from "@/components/site/SiteFooter";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-paper text-ink font-sans" data-testid="privacy-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-14 lg:py-20">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-1.5 text-[12px] text-ink-soft hover:text-forest mb-6 transition-colors"
        >
          <ArrowLeft size={13} /> Volver al inicio
        </Link>
        <p className="text-[11px] tracking-[0.28em] uppercase text-copper font-medium">
          Aviso de Privacidad Integral · Versión 1.0 · Vigente desde el 1 de febrero de 2026
        </p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[1.05] text-balance">
          Aviso de Privacidad
        </h1>
        <p className="mt-4 text-[14px] text-ink-soft leading-relaxed">
          De conformidad con la <strong>Ley Federal de Protección de Datos
          Personales en Posesión de los Particulares</strong> (LFPDPPP), su
          Reglamento y los Lineamientos del Aviso de Privacidad emitidos por
          el INAI, hacemos de su conocimiento lo siguiente:
        </p>

        <article className="mt-10 flex flex-col gap-8 text-[14px] leading-relaxed text-ink-soft">
          <Section n="1" title="Responsable del tratamiento">
            El responsable del tratamiento de sus datos personales es{" "}
            <strong>Marketing de Inteligencia Artificial S. de R.L. de C.V.</strong>{" "}
            (en adelante, <em>“unaTESIS”</em>), con domicilio en Av. Américas
            1545, pisos 20, 21, 22 y 23, Col. Providencia, C.P. 44630,
            Guadalajara, Jalisco, México. El C.{" "}
            <strong>Ricardo Zermeño Mejía</strong> funge como responsable
            interno de la protección de datos personales y atención al
            titular, a través del correo{" "}
            <a href="mailto:contacto@unatesis.com" className="text-forest underline">
              contacto@unatesis.com
            </a>
            .
          </Section>

          <Section n="2" title="Datos personales que recabamos">
            Para las finalidades señaladas, unaTESIS recaba las siguientes
            categorías de datos personales:
            <ul className="mt-2 list-disc pl-5 flex flex-col gap-1.5">
              <li>
                <strong>Identificación:</strong> nombre completo, fotografía
                de perfil (si la proporciona vía Google).
              </li>
              <li>
                <strong>Contacto:</strong> correo electrónico.
              </li>
              <li>
                <strong>Académicos:</strong> país, universidad, carrera o
                facultad, etapa de avance, título tentativo de tesis y
                contenido textual que cargue al editor.
              </li>
              <li>
                <strong>Técnicos:</strong> dirección IP, tipo de navegador,
                sistema operativo, identificadores de sesión, eventos de uso
                (heartbeats, sellos criptográficos) y registros de actividad.
              </li>
              <li>
                <strong>Pagos:</strong> los datos financieros (tarjeta,
                facturación) son recabados y procesados directamente por
                <em> Stripe</em>; unaTESIS no almacena dicha información.
              </li>
            </ul>
            <p className="mt-3">
              <strong>No</strong> se recaban datos personales sensibles. No
              está dirigido a menores de edad sin consentimiento del tutor.
            </p>
          </Section>

          <Section n="3" title="Finalidades primarias (necesarias)">
            Los datos serán utilizados para:
            <ul className="mt-2 list-disc pl-5 flex flex-col gap-1.5">
              <li>Crear, autenticar y mantener la cuenta del Usuario.</li>
              <li>
                Prestar los servicios contratados: editor con asistencia IA,
                recuperación de fuentes científicas, validación de coherencia,
                simulador de jurado, exportación de documentos y sellado
                criptográfico SHA-256.
              </li>
              <li>Procesar pagos a través de Stripe y emitir comprobantes.</li>
              <li>
                Brindar soporte técnico, atender dudas o solicitudes vía correo
                electrónico.
              </li>
              <li>
                Cumplir con obligaciones legales, fiscales y administrativas.
              </li>
              <li>
                Asegurar la integridad del servicio y prevenir fraude o uso
                indebido.
              </li>
            </ul>
          </Section>

          <Section n="4" title="Finalidades secundarias (opcionales)">
            Con su consentimiento expreso, podremos utilizar sus datos para:
            <ul className="mt-2 list-disc pl-5 flex flex-col gap-1.5">
              <li>
                Envío de comunicaciones académicas, novedades y promociones
                relacionadas con la Plataforma.
              </li>
              <li>
                Estudios estadísticos anónimos para mejorar el producto.
              </li>
              <li>
                Solicitudes voluntarias de testimonios o casos de éxito.
              </li>
            </ul>
            <p className="mt-3">
              Si no desea que sus datos se utilicen para estas finalidades,
              puede manifestarlo enviando un correo a{" "}
              <a href="mailto:contacto@unatesis.com" className="text-forest underline">
                contacto@unatesis.com
              </a>{" "}
              con el asunto{" "}
              <em>“No deseo recibir comunicaciones secundarias”</em>. La
              negativa no condiciona la prestación del servicio.
            </p>
          </Section>

          <Section n="5" title="Transferencias de datos">
            Sus datos podrán ser transferidos, sin requerir su consentimiento
            adicional cuando lo permita la LFPDPPP, a:
            <ul className="mt-2 list-disc pl-5 flex flex-col gap-1.5">
              <li>
                <strong>Stripe Inc.</strong> (procesamiento de pagos) — bajo
                sus propios estándares PCI-DSS.
              </li>
              <li>
                <strong>Anthropic, PBC</strong> y <strong>OpenAI, OpCo, LLC</strong>{" "}
                — proveedores de modelos de lenguaje (Claude y GPT). El texto
                de la tesis y los prompts asociados se procesan en sus
                servidores para generar respuestas. Ambos están sujetos a
                cláusulas contractuales que prohíben el uso de datos del
                Usuario para entrenamiento de modelos.
              </li>
              <li>
                <strong>OpenAlex</strong> y <strong>Semantic Scholar</strong>{" "}
                — únicamente reciben términos de búsqueda académica; nunca el
                texto de su tesis.
              </li>
              <li>
                <strong>Autoridades competentes</strong>, cuando exista un
                requerimiento legal fundado.
              </li>
            </ul>
          </Section>

          <Section n="6" title="Derechos ARCO">
            Usted tiene derecho a <strong>Acceder</strong>, <strong>Rectificar</strong>,{" "}
            <strong>Cancelar</strong> u <strong>Oponerse</strong> al
            tratamiento de sus datos personales, así como a revocar el
            consentimiento. Para ejercerlos, envíe un correo a{" "}
            <a href="mailto:contacto@unatesis.com" className="text-forest underline">
              contacto@unatesis.com
            </a>{" "}
            con la leyenda <em>“Solicitud de derechos ARCO”</em>, anexando:
            <ul className="mt-2 list-disc pl-5 flex flex-col gap-1.5">
              <li>Nombre completo y correo registrado en la cuenta.</li>
              <li>
                Identificación oficial vigente (INE/IFE o pasaporte) escaneada.
              </li>
              <li>Descripción clara y precisa del derecho que ejerce.</li>
              <li>Cualquier documento que sustente su petición.</li>
            </ul>
            <p className="mt-3">
              Daremos respuesta en un plazo no mayor a <strong>20 días
              hábiles</strong> contados a partir de la recepción de la
              solicitud completa, conforme al artículo 32 de la LFPDPPP. La
              respuesta se entregará por el mismo medio.
            </p>
          </Section>

          <Section n="7" title="Mecanismos para limitar uso o divulgación">
            Adicionalmente, usted puede solicitar la inclusión de sus datos en
            un listado de exclusión de comunicaciones comerciales, o
            configurar las preferencias de cookies y notificaciones desde su
            cuenta. También puede inscribirse en el Registro Público para
            Evitar Publicidad de PROFECO.
          </Section>

          <Section n="8" title="Uso de cookies y tecnologías similares">
            La Plataforma utiliza cookies y tecnologías análogas para mantener
            la sesión, recordar preferencias de idioma y medir el rendimiento
            del producto. No utilizamos cookies de terceros con fines
            publicitarios. Puede deshabilitar las cookies desde la
            configuración de su navegador, aunque ello podría afectar la
            funcionalidad del servicio.
          </Section>

          <Section n="9" title="Conservación de los datos">
            Sus datos personales se conservarán mientras la cuenta esté
            activa. Tras la cancelación, los datos se mantendrán bloqueados
            durante el plazo de prescripción legal aplicable (general 5 años
            en materia civil y 10 años en materia mercantil/fiscal) y luego
            serán suprimidos de forma segura, salvo aquellos que deban
            conservarse por mandato de ley.
          </Section>

          <Section n="10" title="Medidas de seguridad">
            Implementamos medidas administrativas, técnicas y físicas
            razonables —incluidas el cifrado de contraseñas con bcrypt, TLS
            1.2+ en tránsito, control de acceso por roles, registros de
            auditoría y sellos SHA-256 inmutables— para proteger sus datos
            contra acceso no autorizado, pérdida, alteración o divulgación.
          </Section>

          <Section n="11" title="Modificaciones al Aviso de Privacidad">
            unaTESIS podrá modificar este Aviso en cualquier momento. Toda
            modificación se publicará en{" "}
            <a href="/privacidad" className="text-forest underline">
              unatesis.com/privacidad
            </a>{" "}
            indicando claramente la fecha de la última actualización. Cambios
            sustanciales se notificarán al correo registrado.
          </Section>

          <Section n="12" title="Aceptación">
            Al utilizar la Plataforma, el Usuario manifiesta haber leído y
            aceptado el presente Aviso de Privacidad en términos del artículo
            8 de la LFPDPPP.
          </Section>
        </article>

        <p className="mt-12 text-[12px] text-ink-soft italic">
          Última actualización: 1 de febrero de 2026 · Responsable interno: C.
          Ricardo Zermeño Mejía.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section data-testid={`privacy-section-${n}`}>
      <h2 className="font-serif text-2xl text-ink mb-2 leading-snug">
        <span className="text-copper font-medium">{n}.</span> {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}
